import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { App, Report } from '../types';
import CooldownButton from '../components/CooldownButton';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

interface AppReport {
  app: App;
  report: Report | null;
  cooldown: number;
  refreshMsg: string | null;
}

const Reports: React.FC = () => {
  const [appReports, setAppReports] = useState<AppReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timersRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const apps = await api.get<App[]>('/apps');
      const reports = await Promise.all(
        apps.map((app) => api.get<Report>(`/reports/${app.appId}`).catch(() => null))
      );
      setAppReports(
        apps.map((app, i) => ({ app, report: reports[i], cooldown: 0, refreshMsg: null }))
      );
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearInterval(timer));
    };
  }, [fetchData]);

  const startCooldownTimer = (appId: string, seconds: number) => {
    const existing = timersRef.current.get(appId);
    if (existing) clearInterval(existing);

    setAppReports((prev) =>
      prev.map((ar) => (ar.app.appId === appId ? { ...ar, cooldown: seconds } : ar))
    );

    const timer = setInterval(() => {
      setAppReports((prev) =>
        prev.map((ar) => {
          if (ar.app.appId !== appId) return ar;
          const next = ar.cooldown - 1;
          if (next <= 0) {
            const t = timersRef.current.get(appId);
            if (t) clearInterval(t);
            timersRef.current.delete(appId);
            return { ...ar, cooldown: 0 };
          }
          return { ...ar, cooldown: next };
        })
      );
    }, 1000);
    timersRef.current.set(appId, timer);
  };

  const handleRefresh = async (appId: string) => {
    setAppReports((prev) =>
      prev.map((ar) => (ar.app.appId === appId ? { ...ar, refreshMsg: null } : ar))
    );
    try {
      const result = await api.post<{ appId: string; message: string }>(`/reports/${appId}/refresh`);
      setAppReports((prev) =>
        prev.map((ar) => (ar.app.appId === appId ? { ...ar, refreshMsg: result.message } : ar))
      );
    } catch (err: any) {
      if (err.retryAfter) startCooldownTimer(appId, err.retryAfter);
      setAppReports((prev) =>
        prev.map((ar) => (ar.app.appId === appId ? { ...ar, refreshMsg: err.message } : ar))
      );
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <div className="space-y-6">
      <div>
        <h1>Reports</h1>
        <p className="text-sm text-slate-500 mt-1">
          AI-generated summaries per application. Cached on a 6-hour cycle and used for the weekly digest.
        </p>
      </div>

      {appReports.length === 0 && (
        <div className="card-padded text-sm text-slate-500">No applications registered.</div>
      )}

      {appReports.map(({ app, report, cooldown, refreshMsg }) => (
        <div key={app.appId} className="card">
          <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b border-slate-200">
            <div>
              <Link to={`/apps/${app.appId}`} className="text-base font-semibold text-slate-900 hover:text-brand-700">
                {app.displayName}
              </Link>
              <div className="text-xs text-slate-500"><code className="kbd">{app.appId}</code></div>
            </div>
            <CooldownButton
              label="Refresh"
              onClick={() => handleRefresh(app.appId)}
              cooldownSeconds={cooldown}
            />
          </div>
          {refreshMsg && (
            <div className="px-5 pt-3 text-xs text-slate-500">{refreshMsg}</div>
          )}
          {report ? (
            <div className="p-5">
              <div className="text-xs text-slate-500 mb-3">
                Generated {new Date(report.generatedAt).toLocaleString()} · {report.triggeredBy} · {report.anomalyCount} anomalies
              </div>
              <div
                className="text-sm leading-relaxed text-slate-800 max-h-72 overflow-y-auto rounded border border-slate-100 p-3 [&_h1]:text-base [&_h2]:text-base [&_h1]:font-semibold [&_h2]:font-semibold [&_p]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_code]:font-mono [&_code]:text-xs [&_code]:bg-slate-100 [&_code]:px-1 [&_code]:rounded"
                dangerouslySetInnerHTML={{ __html: report.reportBody }}
              />
            </div>
          ) : (
            <div className="p-5 text-sm text-slate-500">No report available.</div>
          )}
        </div>
      ))}
    </div>
  );
};

export default Reports;
